import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import Index from './views/Index.vue';
import PageNotFound from './views/PageNotFound.vue';

// Auth
import Login from './views/auth/Login.vue';

Vue.use(VueRouter);

const routes: RouteConfig[] = [
	{
		path: '/',
		name: 'Home',
		component: Index,
		redirect: 'menus',
		children: [
			{
				path: 'locations',
				name: 'Locations',
				component: () => import(/* webpackChunkName: "locations" */ './views/Locations.vue')
			},
			{
				path: 'locations/enterprise-pull',
				name: 'EnterpriseMenuPull',
				component: () => import(/* webpackChunkName: "enterprise-pull" */ './views/EnterpriseMenuPull.vue')
			},

			// ================================================================
			// Menus
			// ================================================================
			{
				path: 'menus',
				name: 'Menus',
				component: () => import(/* webpackChunkName: "menus" */ './views/Menus.vue')
			},
			{
				path: '/menus/:menu_id/items',
				name: 'MenuItems',
				component: () => import(/* webpackChunkName: "menu-items" */ './views/Menus.vue'),
				props: route => {
					return { menu_id: parseInt(route.params.menu_id) };
				}
			},
			{
				path: '/menus/:menu_id/sections',
				name: 'Sections',
				component: () => import(/* webpackChunkName: "sections" */ './views/Menus.vue'),
				props: route => {
					return { menu_id: parseInt(route.params.menu_id) };
				}
			},
			{
				path: '/menus/:menu_id/sections/:section_id/items',
				name: 'SectionItems',
				component: () => import(/* webpackChunkName: "section-items" */ './views/Menus.vue'),
				props: route => {
					return { menu_id: parseInt(route.params.menu_id), section_id: parseInt(route.params.section_id) };
				}
			},
			{
				path: '/availabilities',
				name: 'Availabilities',
				component: () => import(/* webpackChunkName: "availability" */ './views/Availabilities.vue')
			},

			// ================================================================
			// Order management
			// ================================================================
			{
				path: '/order-management',
				name: 'OrderManagement',
				component: () => import(/* webpackChunkName: "order-management" */ './views/OrderManagement.vue')
			},
			{
				path: '/order-management/create',
				name: 'CreateOrder',
				component: () => import(/* webpackChunkName: "create-order" */ './views/Orders.vue')
			},
			{
				path: '/order-management/customer-display',
				name: 'OrdersStatusScreen',
				component: () => import(/* webpackChunkName: "orders-status-screen" */ './views/OrdersStatusScreen.vue'),
				meta: { hideNavbar: true }
			},
			{
				path: '/order-management/:order_id',
				name: 'EditOrder',
				component: () => import(/* webpackChunkName: "edit-order" */ './views/Orders.vue')
			},
			{
				path: '/order-management/:order_id/beo',
				name: 'CateringBeo',
				component: () => import(/* webpackChunkName: "catering-beo" */ './views/CateringBeo.vue')
			},

			// ================================================================
			// Settings
			// ================================================================
			{
				path: '/settings',
				name: 'Settings',
				component: () => import(/* webpackChunkName: "settings" */ './views/Settings.vue')
			},
			{
				path: '/settings/menu-groups',
				name: 'MenuGroups',
				component: () => import(/* webpackChunkName: "menu-groups" */ './views/MenuGroups.vue')
			},

			// ================================================================
			// Multi-location settings
			// ================================================================
			{
				path: '/multi-location-settings',
				name: 'MultiLocationSettings',
				component: () => import(/* webpackChunkName: "multi-location-settings" */ './views/MultiLocationSettings.vue'),
				meta: { hideNavbar: true }
			},
			{
				path: '/multi-location-settings/user-management',
				name: 'UserManagement',
				component: () => import(/* webpackChunkName: "user-management" */ './views/UserManagement.vue'),
				meta: { hideNavbar: true }
			},
			{
				path: '/multi-location-settings/user-management/:user_id',
				name: 'UserProfile',
				component: () => import(/* webpackChunkName: "user-profile" */ './views/UserProfile.vue'),
				meta: { hideNavbar: true }
			},
			{
				path: '/multi-location-settings/menu',
				name: 'BulkEditMenu',
				component: () => import(/* webpackChunkName: "bulk-edit-menu" */ './views/BulkEditMenu.vue'),
				meta: { hideNavbar: true }
			},
			{
				path: '/multi-location-settings/settings',
				name: 'BulkEditSettings',
				component: () => import(/* webpackChunkName: "bulk-edit-settings" */ './views/BulkEditSettings.vue'),
				meta: { hideNavbar: true }
			},

			// ================================================================
			// Events
			// ================================================================
			{
				path: '/events',
				name: 'Events',
				component: () => import(/* webpackChunkName: "events" */ './views/suites/Events.vue')
			},
			{
				path: '/events/all',
				name: 'AllEvents',
				component: () => import(/* webpackChunkName: "all-events" */ './views/suites/AllEvents.vue')
			},
			{
				path: '/events/create',
				name: 'CreateEvent',
				component: () => import(/* webpackChunkName: "create-event" */ './views/suites/CreateEvent.vue')
			},
			{
				path: '/events/:event_id',
				name: 'EventDetails',
				component: () => import(/* webpackChunkName: "events" */ './views/suites/EventDetails.vue')
			},
			{
				path: '/events/:event_id/suites',
				name: 'SuiteAssignments',
				component: () => import(/* webpackChunkName: "suite-assignments" */ './views/suites/SuiteAssignments.vue')
			},
			{
				path: '/events/:event_id/suites/:suite_id',
				name: 'SuiteSettings',
				component: () => import(/* webpackChunkName: "suite-assignments" */ './views/suites/SuiteSettings.vue')
			},
			{
				path: '/events/:event_id/suites/:suite_id/beo',
				name: 'Beo',
				component: () => import(/* webpackChunkName: "beo" */ './views/suites/Beo.vue')
			},
			{
				path: '/events/:event_id/production-sheet',
				name: 'ProductionSheet',
				component: () => import(/* webpackChunkName: "production-sheet" */ './views/suites/ProductionSheet.vue')
			},
			{
				path: '/events/:event_id/edit',
				name: 'EventDetailsEdit',
				component: () => import(/* webpackChunkName: "events" */ './views/suites/EditEvent.vue')
			},
			{
				path: '/event-settings',
				name: 'EventSettings',
				component: () => import(/* webpackChunkName: "event-settings" */ './views/suites/EventSettings.vue')
			},
			{
				path: '/event-settings/suite-owners',
				name: 'SuiteOwners',
				component: () => import(/* webpackChunkName: "suite-owners" */ './views/suites/SuiteOwners.vue')
			},
			{
				path: '/event-settings/event-types',
				name: 'EventTypes',
				component: () => import(/* webpackChunkName: "event-types" */ './views/suites/EventTypes.vue')
			},
			{
				path: '/orders-report',
				name: 'OrdersReport',
				component: () => import(/* webpackChunkName: "orders-report" */ './views/OrdersReport.vue')
			},
			{
				path: '/dashboard',
				name: 'Dashboard',
				component: () => import(/* webpackChunkName: "dashboard" */ './views/Dashboard.vue')
			},
			{
				path: '/inquiries',
				name: 'Inquiries',
				component: () => import(/* webpackChunkName: "inquiries" */ './views/Inquiries.vue')
			},

			// ================================================================
			// Rally Intelligence
			// ================================================================
			{
				path: '/rally-intelligence',
				name: 'RallyIntelligence',
				component: () => import(/* webpackChunkName: "rally-intelligence" */ './views/RallyIntelligence.vue')
			}
		]
	},
	{
		path: '/login',
		name: 'Login',
		component: Login
	},
	{
		path: '/login/callback',
		name: 'LoginCallback',
		component: () => import(/* webpackChunkName: "login-callback" */ './views/auth/LoginCallback.vue')
	},
	{
		path: '*',
		component: PageNotFound
	}
];

const router = new VueRouter({
	mode: 'history',
	base: process.env.BASE_URL,
	routes
});

export default router;
